import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import {
  // ABOUT_US,
  HOME,
  OFFERS,
  PROFILE,
  LOGIN,
  REGISTER,
  AGENDA,
  RECHARGES,
  SETTING,
} from '../../routes/app/paths';
import useAuthContext from '../../hooks/useAuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import Avatar from '../../pages/Profile/Avatar';
import Notification from '../Notification/Notification';
import { useTranslation } from 'react-i18next';
import { getAvatar } from '../../utils/common';
import GradientBackground from '../Common/Background/GradientBackground';
import CustomButton from '../Common/CustomButton';
import SVGLogo from '../../assets/svg/logo.svg';
import { LogoutService } from '../../services/auth/AuthService';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import PointsBadge from './headerAtoms/PointsBadge';

export default function Header() {
  const { user, isAuthenticated, logout } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  const userMenuRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Habilitar transiciones solo en browser real, no durante Puppeteer.
    // window.__PRERENDER__ es true durante la captura (vite.config.js).
    // El double-RAF garantiza que isMobile ya pintó su valor real antes
    // de activar las transiciones, evitando animación involuntaria en el primer render.
    // Usamos estado React (no DOM directo) para que el HTML prerenderizado no capture
    // el transition y evitar hydration mismatch.
    if (!window.__PRERENDER__) {
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
        });
      });
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('scroll', handleScroll);
      };
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      if (user?.access_token) {
        await LogoutService({ token: user.access_token });
      }
    } catch {
      // silencioso
    } finally {
      logout();
      navigate(HOME);
    }
  };

  const effectiveMobile = mounted && isMobile;

  const headerStyle = {
    paddingLeft: scrolled ? '0' : 'max(1rem, calc((100% - 100rem) / 2 + 1rem))',
    paddingRight: scrolled ? '0' : 'max(1rem, calc((100% - 100rem) / 2 + 1rem))',
    paddingTop: scrolled ? '0' : (effectiveMobile ? '10px' : '81px'),
    marginRight: scrolled ? '0' : (effectiveMobile ? '6px' : '60px'),
    marginLeft: scrolled ? '0' : (effectiveMobile ? '6px' : '60px'),
    ...(transitionEnabled && { transition: 'padding 0.35s ease, margin 0.35s ease' }),
  };

  const gradientStyle = {
    ...(transitionEnabled && { transition: 'border-radius 0.35s ease' }),
  };

  const navigation = [
    { key: 1, name: t('header.routes.offers'), href: OFFERS },
    // { key: 2, name: t('header.routes.contact'), href: ABOUT_US },
    { key: 3, name: t('header.routes.clientArea'), href: PROFILE },
  ];

  return (
    <header style={headerStyle}>
      <GradientBackground
        color1="#EAF1F1"
        color2="#878B8B"
        fadeToTransparent
        style={gradientStyle}
        className={`flex justify-center shadow-lg 2xl:px-10 backdrop-blur-md ${scrolled ? 'rounded-none' : 'rounded-full'}`}
      >
        <div className="justify-between w-full items-center select-none flex space-x-2 sm:space-x-3 lg:space-x-4 mr-2 lg:px-10 2xl:px-0">
          <Link to={HOME + '#init'} className="flex flex-shrink-0 ml-3 lg:ml-0 items-center gap-2">
            <img
              className="-m-1 sm:-m-1.5 p-1 sm:p-1.5 w-9 lg:w-12 aspect-auto"
              alt="logo"
              src={SVGLogo}
            />
            <span className="text-xl lg:text-3xl text-primary font-light">TropiPhone</span>
          </Link>

          <nav
            className="flex items-center justify-between py-6 sm:py-7 lg:py-6 px-2 sm:px-3 lg:px-0 space-x-2 sm:space-x-3 flex-grow lg:flex-grow-0"
            aria-label="Global"
          >
            <div className="hidden lg:flex items-center gap-3 xl:gap-x-8">
              {navigation.map(
                (item) =>
                  item.key !== 3 && (
                    <NavLink
                      to={item.href}
                      key={item.name}
                      className={
                        item.style
                          ? item.style
                          : 'inline-flex h-auto items-center px-1 text-sm lg:text-base font-bold text-primary whitespace-nowrap'
                      }
                    >
                      {item.name}
                    </NavLink>
                  ),
              )}

              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate(LOGIN)}
                    className="flex py-1 px-3 lg:px-5 items-center text-sm lg:text-base border-primary border-2 rounded-full text-primary font-semibold hover:cursor-pointer hover:shadow-md active:text-gray-700 active:ring-gray-700 whitespace-nowrap"
                  >
                    {t('header.login')}
                  </button>

                  <CustomButton
                    primary
                    onClick={() => navigate(REGISTER)}
                    className="inline-flex h-auto py-1 px-3 lg:px-5 items-center text-sm lg:text-base font-semibold rounded-full text-white hover:cursor-pointer hover:shadow-md active:bg-primary/60 whitespace-nowrap"
                    text={t('header.register')}
                  />
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <NavLink
                    to={RECHARGES}
                    className={({ isActive }) =>
                      `inline-flex h-auto items-center px-1 text-sm lg:text-base font-bold whitespace-nowrap ${isActive ? 'text-primary/70' : 'text-primary'}`
                    }
                  >
                    {t('header.routes.recharges') || 'Historial de recargas'}
                  </NavLink>
                  <NavLink
                    to={AGENDA}
                    className={({ isActive }) =>
                      `inline-flex h-auto items-center px-1 text-sm lg:text-base font-bold whitespace-nowrap ${isActive ? 'text-primary/70' : 'text-primary'}`
                    }
                  >
                    {t('header.routes.agenda') || 'Agenda'}
                  </NavLink>
                  <PointsBadge />
                  <Notification />
                  <div className="relative" ref={userMenuRef}>
                    <CustomButton
                      primary
                      onClick={() => setShowUserMenu((prev) => !prev)}
                      className="flex items-center justify-center px-4 lg:px-5 py-1 lg:py-1.5 text-white rounded-full hover:shadow-md active:opacity-90 flex-shrink-0"
                      ariaLabel={t('header.openProfile')}
                      icon={
                        <Avatar
                          src={getAvatar(user?.avatar)}
                          alt={user?.name}
                          size="w-6 lg:w-7 h-6 lg:h-7"
                          className="rounded-full mr-3"
                        />
                      }
                      text={user?.name}
                    />
                    {showUserMenu && (
                      <div className="absolute right-0 top-full mt-2 w-60 rounded-2xl bg-white shadow-xl border border-slate-100 overflow-hidden" style={{ zIndex: 9999 }}>
                        {/* Info usuario */}
                        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100">
                          <Avatar
                            src={getAvatar(user?.avatar)}
                            alt={user?.name}
                            size="w-10 h-10"
                            className="rounded-full flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">
                              {user?.name}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                          </div>
                        </div>
                        {/* Opciones */}
                        <div className="py-1">
                          <button
                            onClick={() => { setShowUserMenu(false); navigate(PROFILE); }}
                            className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <UserCircleIcon className="w-5 h-5 text-slate-400" />
                            {t('header.userMenu.clientArea')}
                          </button>
                          <button
                            onClick={() => { setShowUserMenu(false); navigate(SETTING); }}
                            className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Cog6ToothIcon className="w-5 h-5 text-slate-400" />
                            {t('header.userMenu.settings')}
                          </button>
                          <button
                            onClick={handleLogout}
                            className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <ArrowRightOnRectangleIcon className="w-5 h-5 text-slate-400" />
                            {t('header.userMenu.logout')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}


            </div>
          </nav>

          {!isAuthenticated ? (
            <button
              onClick={() => navigate(LOGIN)}
              className="flex lg:hidden flex-shrink-0 py-1 sm:py-1.5 px-2 sm:px-3 mr-2 items-center text-xs sm:text-sm border-primary border-2 rounded-full text-primary font-semibold hover:cursor-pointer hover:shadow-md active:text-gray-700 active:ring-gray-700"
            >
              {t('header.login')}
            </button>
          ) : (
            <div className="flex lg:hidden items-center gap-2 mr-2">
              <div className="relative" ref={!mounted || !isMobile ? undefined : userMenuRef}>
                <CustomButton
                  primary
                  onClick={() => setShowUserMenu((prev) => !prev)}
                  className="flex flex-shrink-0 items-center justify-center p-1 text-white rounded-full hover:shadow-md active:opacity-90"
                  ariaLabel={t('header.openProfile')}
                  icon={
                    <Avatar
                      src={getAvatar(user?.avatar)}
                      alt={user?.name}
                      name={user?.name}
                      size="w-7 h-7"
                      className="rounded-full"
                    />
                  }
                />
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white shadow-xl border border-slate-100 overflow-hidden" style={{ zIndex: 9999 }}>
                    <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100">
                      <Avatar
                        src={getAvatar(user?.avatar)}
                        alt={user?.name}
                        size="w-9 h-9"
                        className="rounded-full flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center px-4 py-3 border-b border-slate-100">
                      <PointsBadge />
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => { setShowUserMenu(false); navigate(PROFILE); }}
                        className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <UserCircleIcon className="w-5 h-5 text-slate-400" />
                        {t('header.userMenu.clientArea')}
                      </button>
                      <button
                        onClick={() => { setShowUserMenu(false); navigate(SETTING); }}
                        className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Cog6ToothIcon className="w-5 h-5 text-slate-400" />
                        {t('header.userMenu.settings')}
                      </button>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5 text-slate-400" />
                        {t('header.userMenu.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowMobileSheet(true)}
                className="flex items-center justify-center w-9 h-9 rounded-full text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
                aria-label="Menú"
              >
                <Bars3Icon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </GradientBackground>

      <Drawer
        isOpen={showMobileSheet}
        placement="right"
        onClose={() => setShowMobileSheet(false)}
      >
        <DrawerOverlay />
        <DrawerContent maxW="280px">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" px={4} py={3}>
            <img src={SVGLogo} alt="TropiPhone" className="w-36" />
          </DrawerHeader>
          <DrawerBody px={0} py={2}>
            <nav className="flex flex-col">
              {navigation.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.href}
                  onClick={() => setShowMobileSheet(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors ${isActive ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50'}`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
              {isAuthenticated && (
                <>
                  <NavLink
                    to={RECHARGES}
                    onClick={() => setShowMobileSheet(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors ${isActive ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50'}`
                    }
                  >
                    {t('header.routes.recharges') || 'Historial de recargas'}
                  </NavLink>
                  <NavLink
                    to={AGENDA}
                    onClick={() => setShowMobileSheet(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors ${isActive ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50'}`
                    }
                  >
                    {t('header.routes.agenda') || 'Agenda'}
                  </NavLink>
                </>
              )}
              {!isAuthenticated && (
                <div className="flex flex-col gap-2 px-5 pt-4">
                  <button
                    onClick={() => { setShowMobileSheet(false); navigate(LOGIN); }}
                    className="w-full py-2 px-4 border-primary border-2 rounded-full text-primary font-semibold text-sm hover:shadow-md"
                  >
                    {t('header.login')}
                  </button>
                  <button
                    onClick={() => { setShowMobileSheet(false); navigate(REGISTER); }}
                    className="w-full py-2 px-4 bg-primary rounded-full text-white font-semibold text-sm hover:shadow-md"
                  >
                    {t('header.register')}
                  </button>
                </div>
              )}
            </nav>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </header>
  );
}
